// src/components/pages/ApplyNow.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function ApplyNow() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    age: "",
    gender: "",
    programType: "",
    level: "",
    duration: "",
    healthIssues: false,
    healthDescription: "",
    goals: [],
    agreeTerms: false,
  });

  const [errors, setErrors] = useState({});

  const programs = ["Endurance Program", "Strength Program", "Sports Program"];
  const levels = ["Beginner", "Advanced"];
  const durations = ["6 Weeks", "8 Weeks", "12 Weeks"];
  const goalsList = [
    "Increase Strength",
    "Enhance Athletic Performance",
    "Build Muscle Mass",
    "Improve Endurance",
    "Prevent Injuries",
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName) newErrors.fullName = "Full name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email";
    if (!formData.phone) newErrors.phone = "Phone number is required";
    else if (!/^\d+$/.test(formData.phone))
      newErrors.phone = "Phone can only contain digits";
    if (!formData.age) newErrors.age = "Age is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.programType) newErrors.programType = "Select a program";
    if (!formData.level) newErrors.level = "Select your level";
    if (!formData.duration) newErrors.duration = "Select duration";
    if (formData.healthIssues && !formData.healthDescription)
      newErrors.healthDescription = "Please describe your health condition";
    if (!formData.goals.length) newErrors.goals = "Select at least one goal";
    if (!formData.agreeTerms) newErrors.agreeTerms = "You must agree to terms";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      navigate("/apply-confirmation", {
        state: { name: formData.fullName },
      });
    }
  };
  return (
    <section className="w-full min-h-screen py-16 flex justify-center items-start">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-3xl bg-black/40 backdrop-blur-md rounded-2xl p-10 flex flex-col gap-6 shadow-xl border border-gray-200"
      >
        <h2 className="text-white text-4xl font-bold mb-6 text-center">
          Apply Now
        </h2>

        {/* Full Name */}
        <div className="flex flex-col">
          <label className="text-white mb-1">Full Name*</label>
          <input
            type="text"
            value={formData.fullName}
            onChange={(e) =>
              setFormData({ ...formData, fullName: e.target.value })
            }
            className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
          />
          {errors.fullName && (
            <span className="text-red-500">{errors.fullName}</span>
          )}
        </div>

        {/* Email */}
        <div className="flex flex-col">
          <label className="text-white mb-1">Email*</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
          />
          {errors.email && <span className="text-red-500">{errors.email}</span>}
        </div>

        {/* Phone */}
        <div className="flex flex-col">
          <label className="text-white mb-1">Phone Number*</label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
            placeholder=""
          />
          {errors.phone && <span className="text-red-500">{errors.phone}</span>}
        </div>

        {/* Age & Gender */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex flex-col flex-1">
            <label className="text-white mb-1">Age*</label>
            <input
              type="number"
              min="10"
              max="100"
              value={formData.age}
              onChange={(e) =>
                setFormData({ ...formData, age: e.target.value })
              }
              className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
            />
            {errors.age && <span className="text-red-500">{errors.age}</span>}
          </div>

          <div className="flex flex-col flex-1">
            <label className="text-white mb-1">Gender*</label>
            <select
              value={formData.gender}
              onChange={(e) =>
                setFormData({ ...formData, gender: e.target.value })
              }
              className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
            >
              <option value="" disabled hidden></option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {errors.gender && (
              <span className="text-red-500">{errors.gender}</span>
            )}
          </div>
        </div>

        {/* Program Type */}
        <div className="flex flex-col">
          <label className="text-white mb-1">Program Type*</label>
          {/* Program Type */}
          <select
            value={formData.programType}
            onChange={(e) =>
              setFormData({ ...formData, programType: e.target.value })
            }
            className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
          >
            <option value="" disabled hidden></option>
            {programs.map((p, i) => (
              <option key={i} value={p}>
                {p}
              </option>
            ))}
          </select>

          {errors.programType && (
            <span className="text-red-500">{errors.programType}</span>
          )}
        </div>

        {/* Level & Duration */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex flex-col flex-1">
            <label className="text-white mb-1">Level*</label>
            <select
              value={formData.level}
              onChange={(e) =>
                setFormData({ ...formData, level: e.target.value })
              }
              className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
            >
              <option value="" disabled hidden></option>
              {/*<option value="">Select Level</option> */}
              {levels.map((l, i) => (
                <option key={i} value={l}>
                  {l}
                </option>
              ))}
            </select>
            {errors.level && (
              <span className="text-red-500">{errors.level}</span>
            )}
          </div>

          <div className="flex flex-col flex-1">
            <label className="text-white mb-1">Duration*</label>
            {/* Duration */}
            <select
              value={formData.duration}
              onChange={(e) =>
                setFormData({ ...formData, duration: e.target.value })
              }
              className="px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700"
            >
              <option value="" disabled hidden></option>
              {durations.map((d, i) => (
                <option key={i} value={d}>
                  {d}
                </option>
              ))}
            </select>

            {errors.duration && (
              <span className="text-red-500">{errors.duration}</span>
            )}
          </div>
        </div>

        {/* Health Issues */}
        <div className="flex flex-col gap-2">
          <label className="text-white mb-1">
            Do you have any health issues?
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-white">
              <input
                type="radio"
                checked={formData.healthIssues === true}
                onChange={() =>
                  setFormData({ ...formData, healthIssues: true })
                }
                className="accent-neonTurquoise"
              />
              Yes
            </label>
            <label className="flex items-center gap-2 text-white">
              <input
                type="radio"
                checked={formData.healthIssues === false}
                onChange={() =>
                  setFormData({
                    ...formData,
                    healthIssues: false,
                    healthDescription: "",
                  })
                }
                className="accent-neonTurquoise"
              />
              No
            </label>
          </div>
          {formData.healthIssues && (
            <textarea
              placeholder="Please describe your health condition"
              value={formData.healthDescription}
              onChange={(e) =>
                setFormData({ ...formData, healthDescription: e.target.value })
              }
              className="px-4 py-2 rounded-lg bg-gray-900 text-gray-400 border border-gray-700"
            />
          )}
          {errors.healthDescription && (
            <span className="text-red-500">{errors.healthDescription}</span>
          )}
        </div>

        {/* Goals */}
        <div className="flex flex-col gap-2">
          <label className="text-white mb-1">Your Goals*</label>
          <div className="flex flex-wrap gap-3">
            {goalsList.map((g, i) => (
              <label
                key={i}
                className="flex text-gray-400 items-center gap-2 bg-gray-900 px-3 py-1 rounded-full cursor-pointer border border-gray-700"
              >
                <input
                  type="checkbox"
                  checked={formData.goals.includes(g)}
                  onChange={() => {
                    const newGoals = formData.goals.includes(g)
                      ? formData.goals.filter((goal) => goal !== g)
                      : [...formData.goals, g];
                    setFormData({ ...formData, goals: newGoals });
                  }}
                  className="accent-neonTurquoise"
                />{" "}
                {g}
              </label>
            ))}
          </div>
          {errors.goals && <span className="text-red-500">{errors.goals}</span>}
        </div>

        {/* Terms */}
        <label className="flex items-center gap-2 text-white">
          <input
            type="checkbox"
            checked={formData.agreeTerms}
            onChange={() =>
              setFormData({ ...formData, agreeTerms: !formData.agreeTerms })
            }
            className="accent-neonTurquoise"
          />
          I agree to the terms and conditions*
        </label>
        {errors.agreeTerms && (
          <span className="text-red-500">{errors.agreeTerms}</span>
        )}

        {/* Submit */}
        <motion.button
          type="submit"
          whileHover={{ scale: 1.05 }}
          className="mt-4 px-8 py-3 bg-neonTurquoise text-black font-bold rounded-full shadow-lg hover:bg-neonTurquoise/80 transition"
        >
          Apply Now
        </motion.button>
      </form>
    </section>
  );
}
